import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useRef, useCallback, Suspense } from "react";
import loadable from "./loadable";
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Reward = loadable(function () {
  return import(/* webpackChunkName: "react-rewards" */"react-rewards");
});

/**
 * @typedef {'confetti' | 'emoji' | 'memphis'} ReactRewardTypes
 */

/**
 * Hook for managing the state for the React Rewards component
 *
 * @param {() => void} onClick
 * @param {() => boolean} shouldReward
 * @param  {...any} deps
 * @returns {[React.MutableRefObject<undefined>, () => void]}
 */
export var useReward = function useReward(onClick, shouldReward) {
  var rewardRef = useRef();

  /** @type {(e: React.SyntheticEvent) => void } */
  var handleClick = useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (rewardRef.current) {
      if (shouldReward === undefined || shouldReward()) {
        rewardRef.current.rewardMe && rewardRef.current.rewardMe();
      }
      if (onClick) {
        onClick();
      }
    }
  }, [onClick, shouldReward]);
  return [rewardRef, handleClick];
};
var LazyRewardFallback = function LazyRewardFallback(_ref) {
  var children = _ref.children,
    onClick = _ref.onClick;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children({
      onClick: onClick
    })
  });
};

/**
 * Component that passes the children a onClick handler via render props,
 * which, when called, the reward will be triggered.
 *
 * @param {{
 *   type?: ReactRewardTypes,
 *   shouldReward?: () => boolean,
 *   onClick?: () => void,
 *   children: ({onClick: () => void}) => React.ReactNode[]
 * }} param0
 */
export var RewardHandler = function RewardHandler(_ref2) {
  var type = _ref2.type,
    children = _ref2.children,
    shouldReward = _ref2.shouldReward,
    onClick = _ref2.onClick;
  if (process.env.SSR || process.env.NODE_ENV === "test") {
    return /*#__PURE__*/_jsx(LazyRewardFallback, {
      onClick: onClick,
      children: children
    });
  } else {
    var _useReward = useReward(onClick, shouldReward),
      _useReward2 = _slicedToArray(_useReward, 2),
      ref = _useReward2[0],
      childClickHandler = _useReward2[1];
    return /*#__PURE__*/_jsx(Suspense, {
      fallback: /*#__PURE__*/_jsx(LazyRewardFallback, {
        onClick: childClickHandler,
        children: children
      }),
      children: /*#__PURE__*/_jsx(Reward, {
        type: type,
        ref: ref,
        children: children({
          onClick: childClickHandler
        })
      })
    });
  }
};

/**
 * A convenient component rendering a Like Button,
 * which shows a animation, when the user liked. it will not show the animation
 * if the user unliked. the default animation type is 'confetti'
 *
 * @param {{
 *  isLiked: boolean,
 *  handleLikeToggle: () => void,
 *  iconStyle?: React.CSSProperties,
 *  btnClassName?: string,
 *  type: ReactRewardTypes,
 *  iconActiveClassName: string,
 *  shouldReward?: boolean
 * }} param0
 */
export var LikeRewardButton = function LikeRewardButton(_ref3) {
  var isLiked = _ref3.isLiked,
    handleLikeToggle = _ref3.handleLikeToggle,
    iconStyle = _ref3.iconStyle,
    _ref3$btnClassName = _ref3.btnClassName,
    btnClassName = _ref3$btnClassName === void 0 ? "btn-default" : _ref3$btnClassName,
    type = _ref3.type,
    _ref3$iconActiveClass = _ref3.iconActiveClassName,
    iconActiveClassName = _ref3$iconActiveClass === void 0 ? "text-primary" : _ref3$iconActiveClass,
    parentShouldReward = _ref3.shouldReward,
    _children = _ref3.children;
  var shouldReward = useCallback(function () {
    return !isLiked && parentShouldReward !== false;
  }, [isLiked, parentShouldReward]);
  return /*#__PURE__*/_jsx(RewardHandler, {
    type: type,
    shouldReward: shouldReward,
    onClick: handleLikeToggle,
    children: function children(_ref4) {
      var onClick = _ref4.onClick;
      return /*#__PURE__*/_jsxs("button", {
        className: "btn ".concat(btnClassName),
        onClick: onClick,
        children: [/*#__PURE__*/_jsx("i", {
          className: "fa ".concat(isLiked ? "fa-thumbs-up " + iconActiveClassName : "fa-thumbs-o-up"),
          style: iconStyle
        }), _children]
      });
    }
  });
};
//# sourceMappingURL=reward-button.js.map